import { motion } from 'framer-motion';
import { Save, Eye, EyeOff, Lock, Sparkles } from 'lucide-react';
import PageError from '../components/PageError';
import WeeklyScheduleEditor from '@/components/settings/WeeklyScheduleEditor';
import { Skeleton } from '../components/ui/skeleton';
import useSettings from '../hooks/trSettings';
import Holidays from './frontHolidays';
import Thresholds from './frontThresholds';

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
      checked ? 'bg-blue-600' : 'bg-gray-200'
    }`}
  >
    <span
      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-4' : 'translate-x-0'
      }`}
    />
  </button>
);

const TabHeader = ({ title, description }) => (
  <div className="mb-6 pb-5 border-b border-gray-100">
    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
  </div>
);

const AdminSettings = () => {
  const {
    user,
    isBasicPlan,
    activeTab,
    setActiveTab,
    initialLoading,
    loadError,
    loading,
    showPassword,
    setShowPassword,
    profileData,
    setProfileData,
    securityData,
    setSecurityData,
    notificationSettings,
    setNotificationSettings,
    schoolData,
    setSchoolData,
    scheduleData,
    setScheduleData,
    tabs,
    handleProfileSubmit,
    handleSecuritySubmit,
    handleNotificationSubmit,
    handleSchoolSubmit,
    handleScheduleSubmit,
  } = useSettings();

  const inputCls =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1.5';
  const saveBtnCls =
    'flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors';
  const cardCls = 'bg-white rounded-xl shadow-sm border border-gray-200 p-6';
  const cardSmCls = 'bg-white rounded-xl shadow-sm border border-gray-200 p-5';
  const planBadgeCls = isBasicPlan
    ? 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600'
    : 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700';

  const renderProfileTab = () => (
    <div className="space-y-4">
      <div className={cardCls}>
        <form onSubmit={handleProfileSubmit} className="space-y-6">
          <TabHeader
            title="Informations personnelles"
            description="Mettez à jour les détails de votre compte administrateur"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Prénom</label>
              <input
                type="text"
                value={profileData.firstName}
                onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Nom</label>
              <input
                type="text"
                value={profileData.lastName}
                onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Téléphone</label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Poste</label>
              <input
                type="text"
                value={profileData.position}
                onChange={(e) => setProfileData({ ...profileData, position: e.target.value })}
                className={inputCls}
                placeholder="Ex: Directeur, Secrétaire..."
              />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={loading} className={saveBtnCls}>
              <Save className="w-4 h-4" />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>

      <div className={cardSmCls}>
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Mon compte</h3>
        <dl className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <dt className="text-sm text-gray-500">Plan</dt>
            <dd>
              <span className={planBadgeCls}>
                {!isBasicPlan && <Sparkles className="w-3 h-3" />}
                {isBasicPlan ? 'Basic' : 'Premium'}
              </span>
            </dd>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <dt className="text-sm text-gray-500">Rôle</dt>
            <dd className="text-sm font-medium text-gray-700">{user?.role || 'Administrateur'}</dd>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <dt className="text-sm text-gray-500">École</dt>
            <dd className="text-sm font-medium text-gray-700 text-right max-w-[200px] truncate">
              {user?.school?.name || schoolData.schoolName || '—'}
            </dd>
          </div>
          <div className="flex items-center justify-between py-2">
            <dt className="text-sm text-gray-500">Email</dt>
            <dd className="text-sm text-gray-700">{profileData.email || user?.email || '—'}</dd>
          </div>
        </dl>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-4">
      <div className={cardCls}>
        <form onSubmit={handleSecuritySubmit} className="space-y-5">
          <TabHeader title="Sécurité du compte" description="Modifiez votre mot de passe de connexion" />
          <div>
            <label className={labelCls}>Mot de passe actuel</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={securityData.currentPassword}
                onChange={(e) =>
                  setSecurityData({
                    ...securityData,
                    currentPassword: e.target.value,
                  })
                }
                className={`${inputCls} pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className={labelCls}>Nouveau mot de passe</label>
            <input
              type="password"
              value={securityData.newPassword}
              onChange={(e) =>
                setSecurityData({
                  ...securityData,
                  newPassword: e.target.value,
                })
              }
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Confirmer le mot de passe</label>
            <input
              type="password"
              value={securityData.confirmPassword}
              onChange={(e) =>
                setSecurityData({
                  ...securityData,
                  confirmPassword: e.target.value,
                })
              }
              className={inputCls}
            />
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={loading} className={saveBtnCls}>
              <Save className="w-4 h-4" />
              {loading ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
            </button>
          </div>
        </form>
      </div>
      <div className={cardSmCls}>
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Bonnes pratiques</h3>
        <ul className="space-y-3">
          {[
            'Utilisez au moins 8 caractères, avec majuscules et chiffres',
            "Ne réutilisez pas un mot de passe d'un autre service",
            'Ne partagez jamais vos identifiants avec un tiers',
            'Changez votre mot de passe régulièrement',
          ].map((tip, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
              <span className="mt-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 shrink-0 text-xs font-semibold">
                {i + 1}
              </span>
              {tip}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const notifRow = (label, description, key) => (
    <div key={key} className="flex items-center justify-between py-3.5 border-b border-gray-50 last:border-0">
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
      </div>
      <Toggle
        checked={notificationSettings[key]}
        onChange={(v) => setNotificationSettings({ ...notificationSettings, [key]: v })}
      />
    </div>
  );

  const renderNotificationTab = () => (
    <div className={cardCls}>
      <form onSubmit={handleNotificationSubmit} className="space-y-6">
        <TabHeader
          title="Préférences de notification"
          description="Choisissez comment et quand vous souhaitez être notifié"
        />
        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Canaux</p>
          </div>
          <div className="px-4">
            {notifRow('Email', 'Recevoir les alertes par email', 'emailNotifications')}
            {notifRow('SMS', 'Recevoir les alertes par SMS', 'smsNotifications')}
            {notifRow('Push mobile', "Notifications sur l'application mobile", 'pushNotifications')}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Types d&apos;alertes</p>
          </div>
          <div className="px-4">
            {notifRow('Alertes bus', 'Retards, pannes, incidents', 'busAlerts')}
            {notifRow("Mises à jour d'itinéraires", 'Changements de circuits', 'routeUpdates')}
            {notifRow("Alertes d'urgence", 'Priorité maximale, toujours actives', 'emergencyAlerts')}
          </div>
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={loading} className={saveBtnCls}>
            <Save className="w-4 h-4" />
            {loading ? 'Enregistrement...' : 'Enregistrer les préférences'}
          </button>
        </div>
      </form>
    </div>
  );

  const renderSchoolTab = () => (
    <div className="space-y-4">
      <div className={cardCls}>
        <form onSubmit={handleSchoolSubmit} className="space-y-6">
          <TabHeader title="Informations de l'école" description="Coordonnées et détails de votre établissement" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <label className={labelCls}>Nom de l&apos;école</label>
              <input
                type="text"
                value={schoolData.schoolName}
                onChange={(e) => setSchoolData({ ...schoolData, schoolName: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Adresse</label>
              <input
                type="text"
                value={schoolData.address}
                onChange={(e) => setSchoolData({ ...schoolData, address: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Téléphone</label>
              <input
                type="tel"
                value={schoolData.phone}
                onChange={(e) => setSchoolData({ ...schoolData, phone: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Email de contact</label>
              <input
                type="email"
                value={schoolData.email}
                onChange={(e) => setSchoolData({ ...schoolData, email: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Site web</label>
              <input
                type="url"
                value={schoolData.website}
                onChange={(e) => setSchoolData({ ...schoolData, website: e.target.value })}
                className={inputCls}
                placeholder="https://..."
              />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={loading} className={saveBtnCls}>
              <Save className="w-4 h-4" />
              {loading ? 'Enregistrement...' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>
      <div className={cardSmCls}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700">Votre abonnement</h3>
          <span className={planBadgeCls}>
            {!isBasicPlan && <Sparkles className="w-3 h-3" />}
            {isBasicPlan ? 'Basic' : 'Premium'}
          </span>
        </div>
        <dl className="space-y-0 divide-y divide-gray-50">
          <div className="flex items-center justify-between py-2.5">
            <dt className="text-sm text-gray-500">Élèves max</dt>
            <dd className="text-sm font-semibold text-gray-800">{isBasicPlan ? '100' : '300'}</dd>
          </div>
          <div className="flex items-center justify-between py-2.5">
            <dt className="text-sm text-gray-500">Chauffeurs max</dt>
            <dd className="text-sm font-semibold text-gray-800">{isBasicPlan ? '5' : '20'}</dd>
          </div>
          <div className="flex items-center justify-between py-2.5">
            <dt className="text-sm text-gray-500">Rétention données GPS</dt>
            <dd className="text-sm font-semibold text-gray-800">{isBasicPlan ? '30 jours' : '365 jours'}</dd>
          </div>
        </dl>
      </div>
    </div>
  );

  const renderScheduleTab = () => (
    <div className={cardCls}>
      <div className="space-y-6">
        <div className="flex items-start justify-between pb-5 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Horaires de la semaine type</h2>
            <p className="text-sm text-gray-500 mt-0.5">Configurez les créneaux horaires appliqués par défaut</p>
          </div>
          <button onClick={handleScheduleSubmit} disabled={loading} className={saveBtnCls}>
            <Save className="w-4 h-4" />
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
        <WeeklyScheduleEditor initialSchedule={scheduleData} onChange={(newSchedule) => setScheduleData(newSchedule)} />
      </div>
    </div>
  );

  if (initialLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-40 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="flex flex-col md:flex-row gap-6 md:items-start">
          <div className="w-full md:w-60 md:shrink-0 space-y-1.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-lg" />
            ))}
          </div>
          <div className="flex-1 space-y-4">
            <Skeleton className="h-64 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Paramètres</h1>
        <p className="text-sm text-gray-500 mt-1">Gérez votre profil et les paramètres de l&apos;école</p>
      </div>

      <div className="flex flex-col md:flex-row gap-6 md:items-start">
        <nav className="w-full md:w-60 md:shrink-0">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 md:sticky md:top-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const locked = tab.isPremium && isBasicPlan;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-0.5 last:mb-0 ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : locked
                        ? 'text-gray-400 hover:bg-gray-50 hover:text-gray-500'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-blue-600' : ''}`} />
                  <span className="flex-1 text-left">{tab.label}</span>
                  {locked && <Lock className="w-3 h-3 shrink-0 text-gray-300" />}
                </button>
              );
            })}
          </div>
        </nav>

        <div className="flex-1 min-w-0">
          {loadError ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <PageError error={loadError} variant="inline" />
            </div>
          ) : (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15 }}
            >
              {activeTab === 'profile' && renderProfileTab()}
              {activeTab === 'security' && renderSecurityTab()}
              {activeTab === 'notifications' &&
                (isBasicPlan ? (
                  <div className={cardCls}>
                    <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
                      <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                        <Lock className="w-7 h-7 text-amber-500" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-900 mb-2">Fonctionnalité Premium</h3>
                      <p className="text-sm text-gray-500 max-w-xs mb-5">
                        La gestion des préférences de notification est réservée à l&apos;offre{' '}
                        <span className="font-medium text-gray-700">Premium</span>. En Basic, les notifications GPS sont
                        actives par défaut.
                      </p>
                      <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
                        <span className="text-xs text-amber-700 font-medium">
                          Passez à Premium pour accéder à ces paramètres
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  renderNotificationTab()
                ))}
              {activeTab === 'school' && renderSchoolTab()}
              {activeTab === 'schedule' && renderScheduleTab()}
              {activeTab === 'holidays' && <Holidays embedded />}
              {activeTab === 'thresholds' && <Thresholds embedded />}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
